import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';

import {
  downloadAidModelResultsDataset,
  getAidModelResults,
  getGetAidModelResultsQueryKey,
} from 'api/generated/aid-simulation-results/aid-simulation-results';
import {
  getAidModelCoa,
  getGetAidModelCoaQueryKey,
} from 'api/generated/aid-simulation-cost-of-attendance-coa/aid-simulation-cost-of-attendance-coa';
import type {
  AidModelCoaDto,
  AidModelResultsResponseDto,
} from 'api/generated/schemas';

import { parseContentDispositionFilename } from 'helpers/api/parse-content-disposition-filename';

type ModelId = string | number;

const toNumber = (value: ModelId): number => Number(value);

interface UseAidResultsParams {
  userId: string;
  modelId: ModelId;
  enabled?: boolean;
}

interface UseAidResultsCoaParams {
  userId: string;
  modelId: ModelId;
  enabled?: boolean;
}

interface DownloadResultsDatasetVars {
  userId: string;
  modelId: ModelId;
}

export { getGetAidModelResultsQueryKey };

export const useAidResults = ({
  userId,
  modelId,
  enabled = true,
}: UseAidResultsParams) =>
  useQuery<AidModelResultsResponseDto>({
    queryKey: getGetAidModelResultsQueryKey(userId, toNumber(modelId)),
    queryFn: ({ signal }) =>
      getAidModelResults(userId, toNumber(modelId), undefined, signal),
    enabled: enabled && !!userId && !!modelId,
    retry: false,
  });

export const useAidResultsCoa = ({
  userId,
  modelId,
  enabled = true,
}: UseAidResultsCoaParams) =>
  useQuery<AidModelCoaDto[]>({
    queryKey: getGetAidModelCoaQueryKey(userId, toNumber(modelId)),
    queryFn: ({ signal }) =>
      getAidModelCoa(userId, toNumber(modelId), undefined, signal),
    enabled: enabled && !!userId && !!modelId,
    // Refetch every time the COA drawer opens (overrides the 30s global default)
    staleTime: 0,
  });

export const useDownloadAidResultsDataset = () =>
  useMutation<void, unknown, DownloadResultsDatasetVars>({
    mutationFn: async ({ userId, modelId }) => {
      const response = await downloadAidModelResultsDataset(
        userId,
        toNumber(modelId),
        { responseType: 'blob' }
      );

      const fileName =
        parseContentDispositionFilename(
          response.headers['content-disposition']
        ) ?? `aid-model-${toNumber(modelId)}-results-dataset.csv`;

      saveAs(response.data, fileName);
    },
    onError: (_error) => {
      // TODO: surface error feedback to the user (toast/banner) when this mutation is wired to UI
    },
  });

export const useInvalidateAidResults = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (userId: string, modelId: ModelId) =>
      queryClient.invalidateQueries({
        queryKey: getGetAidModelResultsQueryKey(userId, toNumber(modelId)),
      }),
    [queryClient]
  );
};
